import type { HoSoThue } from '@qlk/shared';
import {
  LOAI_THUE_TNCN_LABEL,
  PHAM_VI_BAO_CAO_THUE,
  PHAM_VI_BAO_CAO_THUE_LABEL,
} from '@qlk/shared';
import { useState } from 'react';

import {
  DataTable,
  EmptyState,
  KhoiMuc,
  Nut,
  O,
  OChon,
  StatusBadge,
  ToolbarBang,
  type CotBang,
} from '../../../ui/index.js';
import { duongDanBaoCaoThue, useListHoSoThue, useThanhVien } from '../api.js';
import { HopHoSoThue } from './HopHoSoThue.js';

/** Người cần khai hồ sơ: ghép danh sách thành viên với hồ sơ đã có. */
interface DongHoSo {
  userId: string;
  tenNguoi: string;
  email: string;
  hoSo: HoSoThue | null;
}

function mocHienTai(): string {
  const d = new Date();
  return `${String(d.getFullYear())}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * Tab "Hồ sơ thuế" của trang Nhân sự & nhóm: Bên B của biên bản chia lợi nhuận
 * và diện thuế TNCN của từng người, cộng báo cáo thuế cho kế toán.
 *
 * Bảng hiện số định danh **đã che bớt** — server không trả bản đầy đủ cho giao
 * diện, chỉ biên bản in đầy đủ.
 */
export function TabHoSoThue() {
  const hoSo = useListHoSoThue();
  const thanhVien = useThanhVien();

  const [sua, setSua] = useState<DongHoSo | null>(null);
  const [phamVi, setPhamVi] = useState<string>('thang');
  const [moc, setMoc] = useState(mocHienTai());

  const theoId = new Map((hoSo.data ?? []).map((h) => [h.userId, h]));
  const dong: DongHoSo[] = (thanhVien.data ?? []).map((t) => ({
    userId: t.id,
    tenNguoi: t.name,
    email: t.email,
    hoSo: theoId.get(t.id) ?? null,
  }));

  const cot: CotBang<DongHoSo>[] = [
    {
      key: 'nguoi',
      header: 'Người',
      render: (r) => (
        <div className="min-w-0">
          <p className="truncate text-co-ban text-chu">{r.hoSo?.hoTenDayDu || r.tenNguoi}</p>
          <p className="truncate text-phu text-chu-phu">{r.email}</p>
        </div>
      ),
    },
    {
      key: 'cccd',
      header: 'CCCD',
      render: (r) => <span className="text-chu-phu">{r.hoSo?.cccd || '—'}</span>,
    },
    {
      key: 'mst',
      header: 'MST cá nhân',
      render: (r) => <span className="text-chu-phu">{r.hoSo?.mstCaNhan || '—'}</span>,
    },
    {
      key: 'tk',
      header: 'Số tài khoản',
      render: (r) => (
        <span className="text-chu-phu">
          {r.hoSo?.soTaiKhoan || '—'}
          {r.hoSo?.nganHang ? ` · ${r.hoSo.nganHang}` : ''}
        </span>
      ),
    },
    {
      key: 'loaiThue',
      header: 'Diện thuế',
      render: (r) =>
        r.hoSo ? (
          <StatusBadge tone={r.hoSo.loaiThue === 'khau_tru_10' ? 'neutral' : 'info'}>
            {LOAI_THUE_TNCN_LABEL[r.hoSo.loaiThue]}
          </StatusBadge>
        ) : (
          <StatusBadge tone="warn">Chưa khai</StatusBadge>
        ),
    },
    {
      key: 'thaoTac',
      header: '',
      align: 'right',
      render: (r) => (
        <Nut
          kieu="phu"
          co="nho"
          onClick={() => {
            setSua(r);
          }}
        >
          {r.hoSo ? 'Sửa' : 'Khai hồ sơ'}
        </Nut>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <KhoiMuc
        tieuDe="Báo cáo thuế cho kế toán"
        moTa="Họ tên, MST, CCCD, thu nhập chịu thuế, diện thuế và thuế đã khấu trừ — chỉ các kỳ đã thanh toán. Số liệu lấy từ ảnh chụp của kỳ, nên sửa hồ sơ hôm nay không làm đổi báo cáo đã nộp."
      >
        <ToolbarBang
          trai={
            <>
              <OChon
                label="Phạm vi"
                value={phamVi}
                onChange={setPhamVi}
                options={PHAM_VI_BAO_CAO_THUE.map((p) => ({
                  value: p,
                  label: PHAM_VI_BAO_CAO_THUE_LABEL[p],
                }))}
              />
              <O
                label={
                  phamVi === 'nam' ? 'Năm (YYYY)' : phamVi === 'quy' ? 'Quý (YYYY-Qn)' : 'Tháng'
                }
                value={moc}
                onChange={setMoc}
                moTa={phamVi === 'nam' ? '2026' : phamVi === 'quy' ? '2026-Q4' : '2026-10'}
              />
            </>
          }
          phai={
            <Nut
              kieu="phu"
              onClick={() => {
                window.location.href = duongDanBaoCaoThue(phamVi, moc.trim());
              }}
            >
              Xuất Excel
            </Nut>
          }
        />
      </KhoiMuc>

      {hoSo.isError ? (
        <div className="rounded-the border border-vien bg-be-mat">
          <EmptyState loi title="Không tải được hồ sơ" description={hoSo.error.message} />
        </div>
      ) : (
        <DataTable
          caption="Hồ sơ thuế của nhân sự"
          columns={cot}
          rows={dong}
          keyOf={(r) => r.userId}
          dangTai={hoSo.isPending || thanhVien.isPending}
          tongSo={dong.length}
          khiRong={
            <EmptyState
              title="Chưa có nhân sự nào"
              description="Mời thành viên ở tab Thành viên trước."
            />
          }
        />
      )}

      {sua && (
        <HopHoSoThue
          userId={sua.userId}
          tenNguoi={sua.tenNguoi}
          dangCo={sua.hoSo}
          onDong={() => {
            setSua(null);
          }}
        />
      )}
    </div>
  );
}
